import TagManager from "react-gtm-module";
import { AppState } from "../rootReducers";
import { IBasketOrderItem, IOrderStep } from "../../../entities/manhattan/order/types";
import { IProduct, PayloadWithProducts, PayloadWithProductsListAndSelectedProduct } from "../../../entities/product/types";
import { createImpressionForItem, formatItemsForPurchase, getBasketItemInfo, getItemOverviewInfo, getOverviewTPInfo, getUserIdAndAgentTypeFromState } from "../../../utilites/gtmUtils";
import { deviceTypes } from "../../../constants/TelcoDeviceTypes";
import { PaymentOptionsType } from "../../../constants/PaymentOptions";
import _ from "lodash";
import { getFlatStructureOfBasketItems } from "../../../utilites/order";

const STANDARD_ANALYTICS_DATA = {
  userType: 'fizicko',
  interactionType: 'web',
  content: 'webshop',
};

abstract class IAnalyticsMiddlewareProxy {

  public static deviceImpressions: (state: AppState, payload: PayloadWithProducts) => void;
  public static deviceClickFromList: (state: AppState, payload: PayloadWithProductsListAndSelectedProduct) => void;
  public static deviceOverview: (state: AppState) => void;
  public static deviceOverviewPlanChange: (state: AppState) => void;
  public static addToBasket: (state: AppState) => void;
  public static emptyCart: (state: AppState) => void;
  public static selectPaymentOption: (state: AppState, paymentOption: PaymentOptionsType) => void;
  public static addShippingInfo: (state: AppState, shippingTier: string) => void;
  public static submitOrder: (state: AppState) => void;
  public static cancelOrder: (state: AppState) => void;
  public static addInsurance: (state: AppState) => void;

}


export default class AnalyticsMiddlewareProxy extends IAnalyticsMiddlewareProxy {

  public static deviceImpressions(state: AppState, payload: PayloadWithProducts): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);
    const { products } = payload;
    const searchParams = state.searchParams.productList;
    const impressions = [];
    const limit = products.length > 20 ? 20 : products.length;

    for (let i = 0; i < limit; i++) {
      const item = createImpressionForItem(searchParams, products[i], i);
      impressions.push(item);
    }

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        ecommerce: {
          currencyCode: 'RSD',
          impressions,
        },
      }
    });
  }

  public static deviceClickFromList(state: AppState, payload: PayloadWithProductsListAndSelectedProduct): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);
    const { products, product } = payload;
    const searchParams = state.searchParams.productList;
    const i = products.findIndex((prod: IProduct) => prod.id === product.id);
    const item = createImpressionForItem(searchParams, product, i);
    const { list } = item;
    delete item.list;

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'productClick',
        ecommerce: {
          click: {
            actionField: { list },
            products: [
              item
            ]
          }
        }
      }
    });
  }

  public static addInsurance(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);
    const { selectedInsurance, hisPolicies, numberPriceRate } = state.productInsurances;
    const insuranceData = hisPolicies.find(insurance => insurance.id === selectedInsurance?.policyId);
    const item = {
      name: insuranceData.policyType.name,
      id: insuranceData.id,
      price: selectedInsurance?.price,
      brand: "Yettel",
      category: "Insurance",
      quantity: numberPriceRate,
    };

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'addToCart',
        ecommerce: {
          add: {
            actionField: { action: "add" },
            products: [
              item
            ]
          }
        }
      }
    });
  }

  public static deviceOverview(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);
    const {
      productData,
      product: { item }
    } = state.productData;

    const overviewPhone = getItemOverviewInfo(item, productData);
    const overviewTPInfo = getOverviewTPInfo(item, productData);

    const productsArray = [overviewPhone, overviewTPInfo];

    if (productData.participation > 0) {
      productsArray.push({
        name: 'CASH',
        brand: 'YETTEL',
        price: Number(productData.participation),
        quantity: 1,
        category: 'CASH',
        id: '1'
      });
    }

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'productDetails',
        ecommerce: {
          detail: {
            products: productsArray
          }
        },
      }
    });
  }

  public static deviceOverviewPlanChange(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'productDetailsPlanChange',
      }
    });
  }

  public static addToBasket(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);
    const {
      productData,
      product: { item }
    } = state.productData;

    const basketProductInfo = getBasketItemInfo(item, productData);
    const basketTPInfo = getOverviewTPInfo(item, productData);

    const productsInBasketArray = [basketProductInfo, basketTPInfo];

    if (productData.participation > 0) {
      productsInBasketArray.push({
        name: 'CASH',
        brand: 'YETTEL',
        price: Number(productData.participation),
        quantity: 1,
        category: 'CASH',
        id: '1'
      });
    }

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'addToCart',
        ecommerce: {
          currencyCode: 'RSD',
          add: {
            products: productsInBasketArray
          }
        }
      }
    });
  }

  public static emptyCart(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);
    const {
      productData,
      product: { item }
    } = state.productData;

    if (typeof item === 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          userId,
          agentType,
          STANDARD_ANALYTICS_DATA,
          event: 'removeFromCart',
          ecommerce: {
            remove: {
              products: null
            }
          }
        }
      });
      return;
    }

    const cartProductInfo = getBasketItemInfo(item, productData);
    const cartTPInfo = getOverviewTPInfo(item, productData);

    const cartProducts = [cartProductInfo, cartTPInfo];

    if (productData.participation > 0) {
      cartProducts.push({
        name: 'CASH',
        brand: 'YETTEL',
        price: Number(productData.participation),
        quantity: 1,
        category: 'CASH',
        id: '1'
      });
    }

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'removeFromCart',
        ecommerce: {
          remove: {
            products: cartProducts
          }
        }
      }
    });
  }

  public static selectPaymentOption(state: AppState, paymentOption: PaymentOptionsType): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);

    const { checkoutData } = state.manhattanOrder;
    const { steps: { previous } } = checkoutData;
    const step = previous.length + 1;
    const { type } = _.last(previous) as IOrderStep;

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'checkoutOption',
        ecommerce: {
          // eslint-disable-next-line
          checkout_option: {
            actionField: {
              step,
              type,
              option: {
                data: { paymentOption }
              }
            }
          }
        }
      }
    });
  }

  public static addShippingInfo(state: AppState, shippingTier: string): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);

    const { checkoutData } = state.manhattanOrder;
    const { steps: { previous } } = checkoutData;
    const step = previous.length + 1;
    const { type } = _.last(previous) as IOrderStep;

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'checkoutOption',
        ecommerce: {
          // eslint-disable-next-line
          checkout_option: {
            actionField: {
              step,
              type,
              option: {
                data: { shippingTier }
              }
            }
          }
        }
      }
    });
  }

  public static submitOrder(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);

    const { productData } = state.productData;

    const orderData = state.manhattanOrder;
    const orderItems = getFlatStructureOfBasketItems(orderData.order.basket);
    const orderType = orderData.order.type;
    const products = formatItemsForPurchase(orderItems, productData, orderType);
    const isSimOnly = orderItems.filter((item: IBasketOrderItem) => deviceTypes.includes(item.type)).length <= 0;
    const affiliation = isSimOnly ? "PAKET" : "UREĐAJ + PAKET";
    const revenue = products.reduce((sum, item) => sum + Number(item.price), 0);
    const actionField = {
      id: orderData.order.order?.referenceId,
      affiliation,
      revenue,
      coupon: '',
    };

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'purchase',
        ecommerce: {
          purchase: {
            actionField,
          },
          products
        }
      }
    });
  }

  public static cancelOrder(state: AppState): void {
    const { userId, agentType } = getUserIdAndAgentTypeFromState(state);

    const transactionData = state.manhattanTransaction.data.details.order;

    TagManager.dataLayer({
      dataLayer: {
        userId,
        agentType,
        ...STANDARD_ANALYTICS_DATA,
        event: 'productCancelation',
        ecommerce: {
          refund: {
            actionField: {
              id: transactionData.referenceId,
            },
          }
        }
      }
    });
  }

}
